import { View, Accordion, Disclosure, DisclosureTitle, DisclosurePanel, Flex, ComboBox, TextField, Checkbox, Button, ActionButton, Heading, Divider, Link, ToastQueue } from '@adobe/react-spectrum';
import { Item } from '@react-stately/collections';
import React, { useState, useEffect, useRef } from 'react';
import { ContactFormData } from '../AddNewPerson';
import { useDependency } from '../../../../../contexts/DependencyProvider';
import { Check, ExclamationTriangle, Plus, Trash } from 'react-bootstrap-icons';
import { CloseIcon, EditIcon } from '../../../../../components/protected/Icons/IconsLib';
import { usePreloadAssets } from '../../../../../hooks/UsePreloadAssets';
import { useViewInfo } from '../../../../../hooks/UseViewInfo';
import { useTranslation } from 'react-i18next';

interface IContactSectionProps {
  contacts: ContactFormData['contacts'];
  onChange: (contacts: ContactFormData['contacts']) => void;
  onAdd: () => void;
  expandedSectionKey: any
  setExpandedSectionKey: (data: any) => void;
  validationErrors: any
  setValidationErrors: (data: any) => void;
  fieldConfig: any
  validationStatus: any
  setValidationStatus: (data: any) => void;
}

export const ContactSection: React.FC<IContactSectionProps> = ({
  contacts,
  onChange,
  onAdd,
  expandedSectionKey,
  setExpandedSectionKey,
  validationErrors,
  setValidationErrors,
  fieldConfig,
  validationStatus,
  setValidationStatus }) => {
  const { store, translateService } = useDependency();
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const { contactSettings } = usePreloadAssets();
  const { isMobile } = useViewInfo();
  const { t } = useTranslation('contacts');
  const _t = (key: string) => t(`addNewCompany.${key}`, { ns: 'contacts' });
  const _tt = (key: string) => t(`contact_common.${key}`, { ns: 'contacts' });
  const _message = (key: string) => t(`contact_messages.${key}`, { ns: 'contacts' });
  const contactOptions = Object.values(contactSettings) as [{
    priority: string | number;
    name: string;
    id: string,
    type: string,
  }];
  //contactOptions.sort((a, b) => parseInt(String(a.priority)) - parseInt(String(b.priority)));
  const inputRefs = useRef<any[]>([]);
  // Handle checkbox changes with smart logic
  const handleCheckboxChange = (index: number, field: 'isPrimary' | 'isMain', value: boolean) => {
    const newContacts = [...contacts];
    const currentContact = newContacts[index];

    if (field === 'isPrimary' && value) {
      // If setting as primary, uncheck all other contacts of the same typeType
      newContacts.forEach((contact, i) => {
        if (i !== index && contact.typeType === currentContact.typeType) {
          contact.isPrimary = false;
        }
      });
    }

    if (field === 'isMain' && value) {
      // If setting as main, uncheck all other contacts regardless of type
      newContacts.forEach((contact, i) => {
        if (i !== index) {
          contact.isMain = false;
        }
      });
    }

    // Set the current contact's checkbox value
    newContacts[index] = { ...currentContact, [field]: value };
    onChange(newContacts);
  };

  useEffect(() => {
    // Skip processing if contacts array is empty
    if (!contacts || contacts.length === 0) return;

    // Deep clone to avoid reference issues
    const newContacts = JSON.parse(JSON.stringify(contacts));
    let hasChanges = false;

    // Group contacts by typeType
    const typeGroups: any = {};

    contacts.forEach((contact, index) => {
      // Handle undefined typeType gracefully
      const typeType = contact.typeType || 'unknown';

      if (!typeGroups[typeType]) {
        typeGroups[typeType] = [];
      }

      typeGroups[typeType].push({ contact, index });
    });

    // Check and fix each type group
    Object.keys(typeGroups).forEach(typeType => {
      const groupContacts = typeGroups[typeType];

      // Check if any contact in this group is already marked as primary
      const hasPrimary = groupContacts.some((item: any) => item.contact.isPrimary === true);

      // If no contact is primary in this group, mark the first one
      if (!hasPrimary && groupContacts.length > 0) {
        const firstContactIndex = groupContacts[0].index;

        // Set as primary but don't disturb other properties
        newContacts[firstContactIndex] = {
          ...newContacts[firstContactIndex],
          isPrimary: true
        };

        hasChanges = true;
      }
    });

    // Check if any contact is marked as main
    const hasMain = contacts.some(contact => contact.isMain === true);

    // If no main contact, mark the first one as main
    if (!hasMain && contacts.length > 0) {
      newContacts[0] = {
        ...newContacts[0],
        isMain: true
      };
      hasChanges = true;
    }

    // Only trigger onChange if we made changes
    if (hasChanges) {
      onChange(newContacts);
    }
  }, [contacts]);

  useEffect(() => {
    inputRefs.current = contacts.map((_, i) => inputRefs.current[i] || React.createRef());
  }, [contacts]);

  useEffect(() => {
    if (inputRefs.current[0]?.current) {
      inputRefs.current[0].current.focus();
    }
  }, [expandedSectionKey])

  useEffect(() => {
    if (contacts.length > 0) {
      const lastIdx = contacts.length - 1;
      setTimeout(() => {
        if (inputRefs.current[lastIdx]?.current) {
          inputRefs.current[lastIdx].current.focus();
        }
      }, 500)
    }
  }, [contacts.length]);

  const [isSectionRequired, setIsSectionRequired] = useState(false);
  const [isConfirmEnabled, setIsConfirmEnabled] = useState(false);
  useEffect(() => {
    let hasErrors = false;
    contacts.map((contact, index) => {
      if (fieldConfig?.email?.required && contact.typeType === '1') {
        if (!contact.content || contact.content.trim() === '') {
          hasErrors = true;
        }
      }

      if (fieldConfig?.mobile?.required && contact.typeType === '3') {
        if (!contact.content || contact.content.trim() === '') {
          hasErrors = true;
        }
      }
    });

    if (hasErrors) {
      setValidationStatus((prev: any) => ({ ...prev, ['contactSection']: false }));
      setIsSectionRequired(true)
      setIsConfirmEnabled(false)
    } else {
      setValidationStatus((prev: any) => ({ ...prev, ['contactSection']: true }));
      setIsConfirmEnabled(true)
    }
  }, [fieldConfig, contacts]);

  const handleRemoveContact = (index: number) => {
    const updatedContacts = contacts.filter((_, i) => i !== index);
    onChange(updatedContacts);
  };

  const handleContactTypeChange = (id: any, index: number, contact: any) => {
    const filteredType = contactOptions.find((x: any) => x.id === id);
    if (filteredType) {
      const newContacts = [...contacts];

      // Determine if the selected type is required
      let isRequired = false;
      if (filteredType.type === '1' && fieldConfig?.email?.required) {
        isRequired = true;
      }
      if (filteredType.type === '3' && fieldConfig?.mobile?.required) {
        isRequired = true;
      }

      // Update the current contact with new type info and requirement flag
      newContacts[index] = {
        ...contact,
        typeType: filteredType.type,
        type: filteredType.id,
        typeName: filteredType.name,
        isRequired // ← This is the dynamic update
      };

      // Check for other primary contacts of this type
      const sameTypeContacts = newContacts.filter(
        (c, i) => i !== index && c.typeType === filteredType.type && c.isPrimary === true
      );

      if (sameTypeContacts.length > 0) {
        if (contact.isPrimary) {
          sameTypeContacts.forEach((sameTypeContact) => {
            const contactIndex = newContacts.findIndex((c) => c === sameTypeContact);
            if (contactIndex !== -1) {
              newContacts[contactIndex] = { ...newContacts[contactIndex], isPrimary: false };
            }
          });
        }
      } else {
        // No other primary contact of this type exists
        const typeContacts = newContacts.filter(c => c.typeType === filteredType.type);
        if (typeContacts.length === 1) {
          newContacts[index].isPrimary = true;
        }
      }

      onChange(newContacts);
    }
  };


  const onEdit = (data: any, index: number) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleSectionExpandedChange = () => {
    setExpandedSectionKey((prevKey: any) => prevKey.has("contactSection") ? new Set() : new Set(["contactSection"]));
  };

  const handleConfirm = () => {
    const errors: any = {};
    let hasErrors = false;
    if (contacts.length === 0) {
      ToastQueue.negative("Contact information reqiured", { timeout: 5000 });
      return;
    }
    const newValidationErrors = contacts.map((contact, index) => {
      let error: any = {};

      if (fieldConfig?.email?.required && contact.typeType === '1') {
        if (!contact.content || contact.content.trim() === '') {
          error.content = _message('email_required');
          hasErrors = true;
        }
      }

      if (fieldConfig?.mobile?.required && contact.typeType === '3') {
        if (!contact.content || contact.content.trim() === '') {
          error.content = _message('mobile_required');
          hasErrors = true;
        }
      }

      return error;
    });

    setValidationErrors(newValidationErrors);

    if (hasErrors) {
      setValidationStatus((prev: any) => ({ ...prev, contactSection: false }));
      return;
    }

    setValidationStatus((prev: any) => ({ ...prev, contactSection: true }));
    setExpandedSectionKey(new Set(['bankSection']));
  };

  const validateRequiredFields = () => {
    const errors: any = {};
    let hasErrors = false;
    if (contacts.length === 0) {
      ToastQueue.negative("Contact information reqiored", { timeout: 5000 });
      return;
    }
    const newValidationErrors = contacts.map((contact, index) => {
      let error: any = {};

      if (fieldConfig?.email?.required && contact.typeType === '1') {
        if (!contact.content || contact.content.trim() === '') {
          error.content = _message('email_required');
          hasErrors = true;
        }
      }

      if (fieldConfig?.mobile?.required && contact.typeType === '3') {
        if (!contact.content || contact.content.trim() === '') {
          error.content = _message('mobile_required');
          hasErrors = true;
        }
      }

      return error;
    });

    if (hasErrors) {
      setValidationStatus((prev: any) => ({ ...prev, contactSection: false }));
      return;
    }

    setValidationStatus((prev: any) => ({ ...prev, contactSection: true }));
  }

  return (
    <Accordion isQuiet expandedKeys={expandedSectionKey} onExpandedChange={() => handleSectionExpandedChange()}>
      <Disclosure id={'contactSection'}>
        <DisclosureTitle UNSAFE_style={{ paddingBlock: 0 }}>
          <Flex justifyContent="space-between" alignItems="center" width="100%">
            <View><span>{_tt(`contact_information`)}</span><span style={{ color: '#B0BEC5', fontFamily: 'Adobe Clean' }}>{isSectionRequired && ` (${_tt('required')})`}</span></View>
            {validationStatus.contactSection ? <Check size={20} color="#1976D2" /> : <ExclamationTriangle size={20} color="#FF9800" />}
          </Flex>
        </DisclosureTitle>
        <DisclosurePanel>
          <Divider size='M' UNSAFE_style={{ marginBottom: 10 }} />
          <Flex direction={'column'} gap="size-100" marginTop={10}>
            {contacts.map((contact, index) => (
              <View key={index}>
                <Flex key={index} direction={{ base: 'column', L: "row" }} gap="size-50" width='100%'>
                  <ComboBox
                    label={_tt("contact_type")}
                    selectedKey={contact.type}
                    onSelectionChange={(key) => handleContactTypeChange(key, index, contact)}
                    width={{ base: '100%', L: '25%' }}
                    isRequired={contact.isRequired}
                  >
                    {contactOptions.map((option) => (
                      <Item key={option.id}>{option.name}</Item>
                    ))}
                  </ComboBox>
                  <TextField
                    ref={inputRefs.current[index]}
                    label={_tt("value")}
                    value={contact.content}
                    onBlur={() => validateRequiredFields()}
                    onChange={(description) => {
                      const newContacts = [...contacts];
                      newContacts[index] = { ...contact, content: description };

                      // Clear validation error when user types
                      if (Array.isArray(validationErrors)) {
                        const newErrors = [...validationErrors];
                        if (newErrors[index]?.content) {
                          newErrors[index] = {};
                          setValidationErrors(newErrors);
                        }
                      }

                      onChange(newContacts);
                    }}
                    width={{ base: '100%', L: '35%' }}
                    isRequired={contact.isRequired}
                    errorMessage={validationErrors[index]?.content}
                    validationState={validationErrors[index]?.content ? 'invalid' : undefined}
                  />

                  <TextField
                    label={_tt('note')}
                    value={contact.description}
                    onChange={(description) => {
                      const newContacts = [...contacts];
                      newContacts[index] = { ...contact, description };
                      onChange(newContacts);
                    }}
                    width={{ base: '100%', L: '35%' }}
                  />
                  <Checkbox
                    UNSAFE_style={{ marginTop: 25, marginRight: 0 }}
                    isSelected={contact.isPrimary}
                    onChange={(isPrimary) => handleCheckboxChange(index, 'isPrimary', isPrimary)}
                  >
                    {_tt('primary')}
                  </Checkbox>
                  <ActionButton
                    UNSAFE_style={{ marginTop: 25 }}
                    isQuiet={isMobile ? false : true}
                    onPress={() => handleRemoveContact(index)}

                  >
                    {isMobile ? _tt('button_remove') : <CloseIcon size={18} />}
                  </ActionButton>
                </Flex>
              </View>
            ))}
            <Flex justifyContent={'space-between'} marginTop={10} gap={'size-50'}>
              <View>
                <Plus size={22} color='#1976D2' /> <Link onPress={onAdd} isQuiet UNSAFE_style={{ color: '#1976D2' }}>{_tt('button_add_contact')}</Link>
              </View>
              <View>
                <ActionButton isDisabled={!isConfirmEnabled} onPress={handleConfirm} isQuiet UNSAFE_style={{ color: !isConfirmEnabled ? '#B0BEC5' : '#1976D2', padding: 10 }}><Check size={22} /> {_tt('button_confirm')}</ActionButton>
              </View>
            </Flex>
          </Flex>
          <Divider size='M' UNSAFE_style={{ marginTop: 10 }} />
        </DisclosurePanel>
      </Disclosure>
    </Accordion>
  );
};